import SBLogo from "@assets/sb-logo.png"
import defaultSchoolBackground from "@assets/default-parents-background-thumbnail.jpg"
import { DropZone, SimpleIconList } from "@components"
import { Button, Card, CardBody, CardHeader, Spinner } from "@heroui/react"
import { ArchiveBoxXMarkIcon, ArrowUpTrayIcon, InformationCircleIcon, PhotoIcon } from "@heroicons/react/24/outline"
import PropTypes from "prop-types"

const Images = ({
  currentAssetType = null,
  currentSelectedFile = null,
  handleCancelUpload, 
  handleDeleteAsset,
  handleFileSelected, 
  handleShowImageUploadArea,
  handleUploadAsset, 
  isAssetDeleting,
  isImageUploadAreaVisible,
  isLoading,
  isUploading,
  logo, 
  schoolBackgroundImage,
  uploadPreviewImage 
}) => (
  <Card className="mt-3">
    <CardHeader className="text-white bg-indigo-700">
      <PhotoIcon className="mr-2 w-14 h-14" />
      <div className="flex items-center justify-between w-full">
        <div className="flex flex-col">
          <h1 className="text-lg font-semibold">Images</h1>
          <p className="text-sm">
            Add images to your site.
          </p>
        </div>

        <div className='flex items-center gap-2'>
          {!isImageUploadAreaVisible && (
            <div className="flex items-center gap-2">
              <Button color="success" isDisabled={isLoading} className="font-semibold text-white" onPress={() => handleShowImageUploadArea("LOGO")}>Select Logo</Button>
              <Button color="success" isDisabled={isLoading} className="font-semibold text-white" onPress={() => handleShowImageUploadArea("LOGIN PARENT BACKGROUND")}>Select Background</Button>
            </div>
          )}

          {isImageUploadAreaVisible && (
            <Button color='danger' onPress={() => handleCancelUpload()}><ArchiveBoxXMarkIcon className="w-[20px] h-[20px]" />Cancel</Button>
          )}

          {uploadPreviewImage && (
            <Button color='success' isLoading={isUploading} className="font-semibold text-white" onPress={() => handleUploadAsset(currentAssetType)}>
              {!isUploading && <ArrowUpTrayIcon className="w-[20px] h-[20px]" />}Upload
            </Button>
          )}
        </div>
      </div>
    </CardHeader>

    <CardBody>
      {isImageUploadAreaVisible && (
        <DropZone 
          handleFileSelected={handleFileSelected}
          icon={<PhotoIcon className="w-24 h-24" />} 
          fileTypes={["jpeg", "jpg", "png"]}
          maxFileSize="200KB" 
          selectedFileForUpload={currentSelectedFile}
        />
      )}

      {!isImageUploadAreaVisible && (
        <div>
          <div className="px-4 pb-3 border-gray-400 border-dotted border-b-1">            
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold">School Logo</h3>
                <p className="mb-3 text-content2-foreground">Appears on the parent login form, event invitation & booking confirmation emails.</p>
                
                <SimpleIconList
                  classNames={{ container: "mr-10" }}
                  items={[
                    { icon: <InformationCircleIcon />, caption: "File Size: Less than 200KB" },
                    { icon: <InformationCircleIcon />, caption: "Allowed Types: jpg / jpeg / png" },
                    { icon: <InformationCircleIcon />, caption: "Recommended Sizes: Dependent on logo orientation" },
                    { icon: <InformationCircleIcon />, caption: "Aspect Ratio: Dependent on logo orientation" },
                  ]}
                  showBorder={false}
                />
              </div>
              
              <div className="relative flex flex-col items-center min-w-40">
                {isLoading ? <Spinner /> : <img src={logo?.filename || SBLogo} alt="Logo" className="w-auto mb-3 max-w-48 max-h-32" />}

                {logo?.filename && (
                  <Button 
                    color="secondary" 
                    size="sm" 
                    variant="bordered" 
                    fullWidth
                    onPress={() => handleDeleteAsset(logo, "LOGO")} isLoading={currentAssetType === "LOGO" && isAssetDeleting}
                  >
                    Revert to default
                  </Button>
                )}
              </div>
            </div>
          </div>
                      
          <div className="px-4 py-3">        
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold">Parent Login Background</h3>
                <p className="mb-3 text-content2-foreground">This image will appear on the parent login form.</p>
                
                <SimpleIconList
                  classNames={{ container: "mr-10" }}
                  items={[
                    { icon: <InformationCircleIcon />, caption: "File Size: Less than 600KB" },
                    { icon: <InformationCircleIcon />, caption: "Allowed Types: jpg / jpeg / png" },
                    { icon: <InformationCircleIcon />, caption: "Recommended Sizes: 1920 x 1080 / 2560 x 1700" },
                    { icon: <InformationCircleIcon />, caption: "Aspect Ratio: 3:2" },
                  ]}
                  showBorder={false}
                />
              </div>
              
              <div className="flex flex-col items-center min-w-40">
                {isLoading ? <Spinner /> : <img src={schoolBackgroundImage?.filename || defaultSchoolBackground} alt="Background" className="w-auto mb-3 max-w-48 max-h-32" />}
                
                {schoolBackgroundImage?.filename && (
                  <Button 
                    color="secondary" 
                    size="sm" 
                    variant="bordered" 
                    onPress={() => handleDeleteAsset(schoolBackgroundImage, "LOGIN PARENT BACKGROUND")} 
                    isLoading={currentAssetType === "LOGIN PARENT BACKGROUND" && isAssetDeleting}
                    fullWidth
                  >
                    Revert to default
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </CardBody>
  </Card>
)

Images.propTypes = {
  currentAssetType: PropTypes.string,
  currentSelectedFile: PropTypes.object,
  handleCancelUpload: PropTypes.func.isRequired,
  handleDeleteAsset: PropTypes.func.isRequired,
  handleFileSelected: PropTypes.func.isRequired,
  handleShowImageUploadArea: PropTypes.func.isRequired,
  handleUploadAsset: PropTypes.func.isRequired,
  isAssetDeleting: PropTypes.bool,
  isImageUploadAreaVisible: PropTypes.bool,
  isLoading: PropTypes.bool,
  isUploading: PropTypes.bool,
  logo: PropTypes.object,
  schoolImage: PropTypes.object,
  uploadPreviewImage: PropTypes.object, 
}

export default Images